import { makeStyles } from "@material-ui/styles";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Skeleton,
} from "@mui/material";
import axios from "axios";
import {
  AutoCompleteComponent,
  FormLabel,
  ModalError,
  ModalSuccess,
  MultipleSelectInputWithTags,
  NumberInput,
  PasswordInput,
  PrimaryButton,
  SecondaryButton,
  TextInput,
} from "components";
import { useEffect, useState } from "react";
import { hardBaseUrl } from "services/urlConstant";
import { arrayToCommaSeparatedString, getErrors, stringTruncate } from "utils";

const useStyles = makeStyles({
  dialog: {
    zIndex: 1,
  },
});

const ModalFormUserPublisher = ({
  open,
  onClose,
  optionPublisher,
  getData,
  preload,
}) => {
  const classes = useStyles();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const [loadingPage, setLoadingPage] = useState(false);
  const [optionRole, setOptionRole] = useState([]);
  const [filteredOptionRole, setFilteredOptionRole] = useState([]);
  const [searchRole, setSearchRole] = useState("");
  const [loadingButton, setLoadingButton] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [payload, setPayload] = useState({
    name: "",
    email: "",
    phone: "",
    role_ids: [],
    publisher_id: "",
    ...(!preload && {
      password: "",
      confirm_password: "",
    }),
  });

  const handleChangePayload = (value, key) => {
    setPayload(currentState => ({
      ...currentState,
      [key]: value,
    }));
    if (key === "password" || key === "confirm_password") {
      setPasswordError(null);
    }
    if (key === "email") {
      setEmailError(null);
    }
  };

  const handleSelectRole = (event, option) => {
    const { checked } = event?.target || false;
    setPayload(prev => ({
      ...prev,
      role_ids: checked
        ? [...prev?.role_ids, option]
        : prev?.role_ids?.filter(item => item?.role_id !== option?.role_id),
    }));
  };

  const getOptionRole = async id => {
    try {
      const res = await axios.get(
        `${hardBaseUrl}/role?type=publisher&publisher_id=${id}`,
        {
          headers,
        }
      );
      setOptionRole(res?.data?.data);
      setFilteredOptionRole(res?.data?.data || []);
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
    }
  };

  const getDetail = async () => {
    setLoadingPage(true);
    try {
      const res = await axios.get(`${hardBaseUrl}/user/${preload}`, {
        headers,
      });
      const { data } = res?.data;
      const requiredKeys = [
        "name",
        "email",
        "phone",
        "role_ids",
        "publisher_id",
      ];
      const filteredData = requiredKeys?.reduce((acc, key) => {
        acc[key] = data[key];
        return acc;
      }, {});

      setPayload({
        ...filteredData,
        publisher_id: data?.party_data?.publisher?.publisher_id,
      });
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };

  const handleSubmit = async () => {
    const roleError = "Please set the role name in the publisher tab";

    if ((preload && optionRole.length === 0) || payload.role_ids.length === 0) {
      return ModalError(roleError);
    }

    const {
      role_ids,
      password,
      confirm_password,
      email,
      ...restPayload
    } = payload;

    if (!preload && password !== confirm_password) {
      return setPasswordError("Password and Confirm Password does not match");
    }

    const method = preload ? axios.put : axios.post;
    const modifiedPayload = {
      email,
      role_ids: role_ids?.map(item => item?.role_id),
      ...restPayload,
      ...(!preload && { password }),
    };

    try {
      setLoadingButton(true);
      await method(
        `${hardBaseUrl}/user${preload ? `/${preload}` : ""}`,
        modifiedPayload,
        { headers }
      );
      onClose();
      ModalSuccess(
        `Successfully ${preload ? "update" : "save"} user publisher`
      ).then(getData);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingButton(false);
    }
  };

  useEffect(() => {
    if (payload.publisher_id) getOptionRole(payload.publisher_id);
  }, [payload?.publisher_id]);

  useEffect(() => {
    if (preload) {
      getDetail();
    }
  }, [preload]);

  useEffect(() => {
    setFilteredOptionRole(
      searchRole
        ? optionRole?.filter(role =>
            role?.name?.toLowerCase().includes(searchRole?.toLowerCase())
          )
        : optionRole
    );
  }, [searchRole]);

  useEffect(() => {
    if (preload) {
      const options = [];
      payload?.role_ids.map(value => {
        optionRole.map(val_cek => {
          if (value === val_cek.role_id) {
            return options.push(val_cek);
          }
        });
      });
      setPayload({
        ...payload,
        role_ids: options,
      });
    }
  }, [preload, optionRole]);

  return (
    <Dialog fullWidth open={open} onClose={onClose} className={classes.dialog}>
      <Box
        fullWidth
        component="form"
        onSubmit={handleSubmit}
        autoComplete="off"
      >
        <DialogTitle>{`${
          preload ? "Edit" : "Add"
        } User Publisher`}</DialogTitle>
        <DialogContent dividers>
          {loadingPage ? (
            <Grid container direction="column" rowSpacing={2}>
              {Object.keys(payload).map((_, index) => (
                <Grid item key={index}>
                  <Box mb="8px">
                    <Skeleton variant="rounded" width="40%" height={25} />
                  </Box>
                  <Skeleton variant="rounded" width="100%" height={30} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid container direction="column" rowSpacing={2}>
              {MockFieldForm({ preload, passwordError, emailError })?.map(
                ({ title, key, type, error }, index) => (
                  <Grid item key={index}>
                    <FormLabel label={title} required />
                    {type === "autocomplete" ? (
                      <AutoCompleteComponent
                        placeholder={title}
                        options={optionPublisher}
                        value={
                          optionPublisher.find(
                            option => option.id === Number(payload?.[key])
                          ) || null
                        }
                        onChange={value => handleChangePayload(value, key)}
                        width="100%"
                        size="small"
                      />
                    ) : type === "select" ? (
                      <MultipleSelectInputWithTags
                        options={filteredOptionRole}
                        placeholder="Choose Role"
                        value={payload?.[key]}
                        textValue={stringTruncate(
                          arrayToCommaSeparatedString(payload?.[key], "name"),
                          40
                        )}
                        optionKey="role_id"
                        optionLabel="name"
                        onChange={handleSelectRole}
                        search
                        searchValue={searchRole}
                        onChangeSearch={setSearchRole}
                        customPaperWidth={552}
                        disabled={loadingButton}
                      />
                    ) : type === "password" ? (
                      <PasswordInput
                        placeholder={title}
                        autoComplete={`new-${key}`}
                        value={payload?.[key]}
                        onChange={e =>
                          handleChangePayload(e?.target?.value, key)
                        }
                        error={!!error}
                        helperText={error}
                        disabled={loadingButton}
                      />
                    ) : type === "phone" ? (
                      <NumberInput
                        placeholder={title}
                        autoComplete={`new-${key}`}
                        value={payload?.[key]}
                        onChange={e => handleChangePayload(e, key)}
                        disabled={loadingButton}
                      />
                    ) : (
                      <TextInput
                        placeholder={title}
                        value={payload?.[key]}
                        autoComplete={`new-${key}`}
                        onChange={e =>
                          handleChangePayload(e?.target?.value, key)
                        }
                        error={!!error}
                        helperText={error}
                        disabled={loadingButton}
                      />
                    )}
                  </Grid>
                )
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <SecondaryButton label="Cancel" onClick={onClose} />
          <PrimaryButton
            label={preload ? "Save" : "Add"}
            loading={loadingButton}
            disabled={loadingButton}
            onClick={handleSubmit}
          />
        </DialogActions>
      </Box>
    </Dialog>
  );
};

const MockFieldForm = ({ preload, passwordError, emailError }) => [
  {
    title: "User Name",
    key: "name",
  },

  {
    title: "Publisher",
    key: "publisher_id",
    type: "autocomplete",
  },
  {
    title: "Role Name",
    key: "role_ids",
    type: "select",
  },
  ...(preload
    ? []
    : [
        {
          title: "Password",
          key: "password",
          type: "password",
          error: passwordError,
        },
        {
          title: "Confirm Password",
          key: "confirm_password",
          type: "password",
          error: passwordError,
        },
      ]),
  {
    title: "Phone Number",
    key: "phone",
    type: "phone",
  },
  {
    title: "Email",
    key: "email",
    error: emailError,
  },
];
export default ModalFormUserPublisher;
